import type { Notification, StoredNotification } from '@cabgo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { wsUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  fetchNotifications,
  isSoundMuted,
  markAllNotificationsRead,
  markNotificationRead,
  playNotificationChime,
  SOUND_MUTED_KEY,
  setSoundMuted,
} from '../lib/notifications';

export function useOperatorNotifications() {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<Notification | null>(null);
  const [muted, setMutedState] = useState<boolean>(() => isSoundMuted());
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    enabled: Boolean(me),
    refetchInterval: 60_000,
  });

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const dismissToast = useCallback(() => {
    setToast(null);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  const showToast = useCallback((notification: Notification) => {
    setToast(notification);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 6000);
  }, []);

  const toggleMute = useCallback(() => {
    setMutedState((prev) => {
      const next = !prev;
      setSoundMuted(next);
      return next;
    });
  }, []);

  // The chime reads localStorage live, so a second tab would otherwise keep a
  // stale `isMuted`: the rail could show "sound on" while audio is suppressed,
  // or show the muted flag while the chime still plays.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      // A null key means localStorage.clear().
      if (event.key !== null && event.key !== SOUND_MUTED_KEY) return;
      setMutedState(isSoundMuted());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const markRead = useCallback(
    async (id: string) => {
      await markNotificationRead(id);
      queryClient.setQueryData<StoredNotification[]>(['notifications'], (old) =>
        (old ?? []).map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    [queryClient],
  );

  const markAllRead = useCallback(async () => {
    await markAllNotificationsRead();
    queryClient.setQueryData<StoredNotification[]>(['notifications'], (old) =>
      (old ?? []).map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })),
    );
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  // Realtime WebSocket channel (/realtime/ws)
  useEffect(() => {
    if (!me) return;

    let socket: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    function connect() {
      if (disposed) return;
      try {
        const url = wsUrl('/realtime/ws');
        socket = new WebSocket(url);

        socket.onmessage = (event) => {
          try {
            const frame = JSON.parse(event.data) as { event?: string; payload?: Notification };
            if (frame.event === 'notification' && frame.payload) {
              void queryClient.invalidateQueries({ queryKey: ['notifications'] });
              showToast(frame.payload);
              playNotificationChime();
            }
          } catch {
            // Ignore non-JSON or heartbeat frames
          }
        };

        socket.onclose = () => {
          if (!disposed) {
            reconnectTimeout = setTimeout(connect, 3000);
          }
        };

        socket.onerror = () => {
          socket?.close();
        };
      } catch {
        if (!disposed) {
          reconnectTimeout = setTimeout(connect, 5000);
        }
      }
    }

    connect();

    return () => {
      disposed = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [me, queryClient, showToast]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isMuted: muted,
    toggleMute,
    markRead,
    markAllRead,
    toast,
    dismissToast,
  };
}
